<template>
  <div>
    <div v-if="isPublicOrMember">
      <div v-if="projectId && !projectLoading" class="">
        <div class="card-body">
          <div class="row justify-content-between">
            <div class="h3 col">Observations</div>
            <div class="col-sm-12 col-lg-3 ml-auto text-right">
              <ProjectNotificationStatus v-if="isMember" notification-type="observation"></ProjectNotificationStatus>
            </div>
          </div>

          <div class="m-1">
            <div class="row align-items-center justify-content-between p-1 filter-container">
              <div class="col-auto d-flex flex-wrap align-items-center">
                <div class="form-inline mr-3 mb-2">
                  <label class="font-weight-bold mr-2 mb-0">View:</label>
                  <div class="btn-group">
                    <button class="btn"
                            :class="observationView === 'list' ? 'btn-tertiary' : 'btn-outline-secondary'"
                            id="ListViewButton"
                            title="List View"
                            @click="setObservationView('list')">
                      <i class="fas fa-list"></i> List
                    </button>
                    <button class="btn"
                            :class="observationView === 'card' ? 'btn-tertiary' : 'btn-outline-secondary'"
                            id="CardViewButton"
                            title="Card View"
                            @click="setObservationView('card')">
                      <i class="far fa-images"></i> Card
                    </button>
                    <button class="btn"
                            :class="observationView === 'map' ? 'btn-tertiary' : 'btn-outline-secondary'"
                            id="MapViewButton"
                            title="Map View"
                            @click="setObservationView('map')">
                      <i class="fa fa-map-marker"></i> Map
                    </button>
                  </div>
                </div>
                <div class="d-lg-none mb-2">
                  <button v-b-modal.observationFilterModal class="btn btn-secondary btn-sm">
                    <i class="fas fa-filter mr-1"></i> All Filters
                  </button>
                </div>
              </div>
              <div class="col-auto mb-2">
                <div class="form-inline">
                  <label class="font-weight-bold mr-2 mb-0">Sort:</label>
                  <!-- w-auto keeps the dropdown from expanding to 100% width -->
                  <b-form-select
                    v-model="selectedSort"
                    :options="sortOptions"
                    class="w-auto custom-select"
                  ></b-form-select>
                </div>
              </div>

            </div>
          </div>

          <div class="row justify-content-between">
            <div class="my-1 col-md-4">
              Showing {{ fromItem }} to {{ toItem }} of
              {{ totalItems }}
              {{
                isManager ? 'observation' :
                  isMember ? 'public and your private observation' :
                    'public observation'
              }}<span v-if="totalItems === 0 || totalItems > 1">s</span>
              <span v-if="isFiltered"> (filtered)</span>
            </div>

            <div class="col-md-3 my-1">
              <b-pagination
                v-if="totalItems > 0"
                :total-rows="totalItems"
                :per-page="perPage"
                v-model="currentPage"
                class="my-0 pagination-holder"
                prev-text="Prev"
                next-text="Next"
                align="fill"
              />
            </div>

            <div class="col-md-3 my-1">
              <b-form-group
                label="Per Page"
                label-align="right"
                label-cols-sm="7"
                label-cols-md="8"
                label-for="perPageSelect"
                class="mb-0"
              >
                <b-form-select
                  v-model="perPage"
                  id="perPageSelect"
                  size="sm"
                  :options="pageOptions"
                  align="right"
                >
                </b-form-select>
              </b-form-group>
            </div>
          </div>
        </div>

        <div class="d-lg-flex">
          <div class="d-none d-lg-block mr-lg-4 ml-1 flex-shrink-0"
               style="width: 280px; position: sticky; top: 20px;">
            <h5 class="ml-2 font-weight-bold border-bottom">Search and Filter</h5>
            <ProjectObservationFilterPanel
              :initial-locations="initialFilterLocations"
              :initial-members="initialFilterMembers"
              :initial-filters="observationFilters"
              @filters-updated="handleFilters"
            />
          </div>

          <div class="flex-grow-1 content-divider">
            <div v-if="observationView !== 'list' && !isBusy">
              <span v-if="isFiltered && totalItems === 0">{{ emptyFilteredText }}</span>
              <span v-if="!isFiltered && totalItems === 0">{{ emptyText }}</span>
            </div>
            <b-row v-if="observationView === 'card'" class="section observation mx-0">
              <div v-if="isBusy">
                <i class="fa fa-spin fa-spinner fa-3x mr-3"></i> Loading Observations...
              </div>
              <ProjectObservationCard
                v-else
                v-for="(observation, index) in observationCards"
                class="m-1"
                :key="index"
                :observation="observation"
                :handle-view-detail="showObservationDetail"
                :handle-view="toObservationPage"
                :handle-delete="onClickDelete">
              </ProjectObservationCard>
            </b-row>

            <b-table
              show-empty
              stacked="md"
              responsive
              id="observationsTable"
              ref="observationsTable"
              :filter="filterVal"
              :busy="isBusy"
              :items="observations"
              :fields="tableFields"
              :current-page="currentPage"
              :per-page="perPage"
              sort-icon-left
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :sort-direction="sortDirection"
              :class="{ 'd-none': observationView !== 'list' }"
              :empty-text="emptyText"
              :empty-filtered-text="emptyFilteredText"
            >
              <template #head(observedAt)>
                  <span id="observations-start-header" class="d-inline-flex align-items-center">
                    Observation Date
                    <i class="fa fa-question-circle ml-1 text-muted"></i>
                  </span>
                <b-tooltip target="observations-start-header" placement="top" triggers="hover focus">
                  Start date of the observation
                </b-tooltip>
              </template>

              <template #head(createdAt)>
                  <span id="observations-created-header" class="d-inline-flex align-items-center">
                    Created Date
                    <i class="fa fa-question-circle ml-1 text-muted"></i>
                  </span>
                <b-tooltip target="observations-created-header" placement="top" triggers="hover focus">
                  When the observation was submitted to CitSci
                </b-tooltip>
              </template>

              <template v-slot:cell(location.name)="row">
                <router-link
                  :to="{ name: 'LocationDetails', params: { id: row.item.location.id } }"
                >
                  {{ row.item.location.name }}
                </router-link>
              </template>
              <template v-slot:cell(createdAt)="row">
                <DateTime :date="row.item.createdAt" :dateTime="false"></DateTime>
              </template>
              <template v-slot:cell(options)="row">
                <b-dropdown
                  right
                  split
                  size="sm"
                  class="m-1"
                  alt="View Details"
                  title="View Details"
                  split-variant="outline-secondary"
                  @click="showObservationDetail(row.item.id)"
                >
                  <template slot="button-content">
                    <i class="fa fa-eye"></i> View Details
                  </template>
                  <b-dropdown-item
                    v-if="isManager"
                    @click="onClickDelete(row.item)"
                    alt="Delete this Observation"
                    title="Delete this Observation"
                  >
                    <i class="fa fa-trash mr-1 mr-lg-2"></i>Delete
                  </b-dropdown-item>
                  <b-dropdown-item
                    alt="View Observation Details in a New Tab"
                    title="View Observation Details in a New Tab"
                    @click="toObservationPage(row.item.id)"
                  >
                    <i class="fa fa-eye"></i> View (new tab) <i class="fas fa-external-link-alt fa-xs"></i>
                  </b-dropdown-item>
                </b-dropdown>
              </template>
              <template #table-busy>
                <i class="fa fa-spin fa-spinner fa-3x mr-3"></i> Loading Observations...
              </template>
            </b-table>

            <div v-if="observationCards && observationView === 'map'" class="m-2">
              <ProjectObservationMap
                :observations="observationCards"
                :is-busy="isBusy"
                :handle-click="showObservationDetail">
              </ProjectObservationMap>
            </div>
          </div>

        </div>
        <div class="card-footer">
          <div class="row justify-content-between">
            <div class="my-1 col-md-4">
              Showing {{ fromItem }} to {{ toItem }} of
              {{ totalItems }}
              {{
                isManager ? 'observation' :
                  isMember ? 'public and your private observation' :
                    'public observation'
              }}<span v-if="totalItems === 0 || totalItems > 1">s</span>
              <span v-if="isFiltered"> (filtered)</span>
            </div>

            <div class="col-md-3 my-1">
              <b-pagination
                v-if="totalItems > 0"
                :total-rows="totalItems"
                :per-page="perPage"
                v-model="currentPage"
                class="my-0 pagination-holder"
                prev-text="Prev"
                next-text="Next"
                align="fill"
              />
            </div>

            <div class="col-md-3 my-1">
              <b-form-group
                label="Per Page"
                label-align="right"
                label-cols-sm="7"
                label-cols-md="8"
                label-for="perPageSelect"
                class="mb-0"
              >
                <b-form-select
                  v-model="perPage"
                  id="perPageSelect"
                  size="sm"
                  :options="pageOptions"
                  align="right"
                >
                </b-form-select>
              </b-form-group>
            </div>
          </div>
        </div>

        <b-modal id="deleteObservationModal" title="Delete Observation" centered>
          <div v-if="selectedObservation">
            <p class="my-4">
              Do you want to remove this observation from the project? This cannot
              be undone.
            </p>
            <div>
              <span class="font-weight-bold">Observed at: </span>
              {{ displayDate(selectedObservation.observedAt) }}
            </div>
            <div>
              <span class="font-weight-bold">Located at: </span>
              {{ selectedObservation.location.name }}
            </div>
          </div>
          <template slot="modal-footer">
            <div>
              <b-btn variant="danger" @click="onConfirmDelete()">Yes, delete</b-btn>
              <b-btn @click="closeModal">No</b-btn>
            </div>
          </template>
        </b-modal>

        <b-modal
          id="observationFilterModal"
          title="Search and Filter"
          hide-footer
          centered
        >
          <div class="p-2">
            <ProjectObservationFilterPanel
              :project-id="projectId"
              :initial-locations="initialFilterLocations"
              :initial-members="initialFilterMembers"
              :initial-filters="observationFilters"
              @filters-updated="handleFilters"
            />
          </div>
        </b-modal>

        <ProjectObservationDetailModal
          :arr-observation-id="detailObservationIds"
          :current-observation-id="currentDetailObservationId || ''"
        ></ProjectObservationDetailModal>

      </div>
    </div>
    <RequiresMember v-else page="Observations"></RequiresMember>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from "vuex";
import VueLoader from "../../../layout/Loader";
import RequiresMember from "../tabpermission/requiresMember.vue";
import DateTime from "../../../../components/common/date-time/DateTime.vue";
import ProjectNotificationStatus from "../notifications/ProjectNotificationStatus.vue";
import ProjectObservationFilterPanel from "./ProjectObservationFilterPanel.vue";
import ProjectObservationMap from "./ProjectObservationMap.vue";
import ProjectObservationDetailModal from "./ProjectObservationDetailModal.vue";
import ProjectObservationCard from "./ProjectObservationCard.vue";

export default {
  name: "ObservationsTable",
  components: {
    VueLoader,
    RequiresMember,
    DateTime,
    ProjectNotificationStatus,
    ProjectObservationFilterPanel,
    ProjectObservationMap,
    ProjectObservationDetailModal,
    ProjectObservationCard,
  },
  data() {
    return {
      pageOptions: [10, 25, 50, 100, 250, 500],
      isBusy: false,
      showModal: null,
      selectedObservation: null,
      observationCards: null,
      currentDetailObservationId: null,
      initialFilterLocations: [],
      initialFilterMembers: [],

      isPhotoCard: false,

      emptyText: "No Observations Available",
      emptyFilteredText: "No Observations Available for Search/Filter Criteria...",

      sortOptions: [
        {text: 'Observation Date: Newest - Oldest', value: {col: 'observedAt', direction: 'desc'}},
        {text: 'Observation Date: Oldest - Newest', value: {col: 'observedAt', direction: 'asc'}},
        {text: 'Created At: Newest - Oldest', value: {col: 'createdAt', direction: 'desc'}},
        {text: 'Created At: Oldest - Newest', value: {col: 'createdAt', direction: 'asc'}},
        {text: 'Datasheet Name: A - Z', value: {col: 'datasheet.name', direction: 'asc'}},
        {text: 'Datasheet Name: Z - A', value: {col: 'datasheet.name', direction: 'desc'}},
        {text: 'Location Name: A - Z', value: {col: 'location.name', direction: 'asc'}},
        {text: 'Location Name: Z - A', value: {col: 'location.name', direction: 'desc'}},
        {text: 'Observed By: A - Z', value: {col: 'user.displayName', direction: 'asc'}},
        {text: 'Observed By: Z - A', value: {col: 'user.displayName', direction: 'desc'}},
      ],

      currentSort: null,

      tableFields: [
        {
          key: "observedAt",
          label: "Observation Date",
          sortable: false,
          sortDirection: "asc",
          tdClass: "text-left",
          formatter: val => {
            return this.displayDate(val);
          }
        },
        {
          key: "createdAt",
          label: "Created Date",
          sortable: false,
          sortDirection: "asc",
          tdClass: "text-left",
        },
        {
          key: "datasheet.name",
          label: "Datasheet",
          sortable: false,
          tdClass: "text-dark",
          formatter: val => {
            return val;
          }
        },
        {
          key: "location.name",
          label: "Location name",
          sortable: false,
          sortDirection: "asc",
          tdClass: "text-left",
          formatter: (val, fieldName, item) => {
            return item && item.location && item.location.name;
          }
        },
        {
          key: "user.displayName",
          label: "Observed By",
          sortable: false,
          sortDirection: "asc",
          tdClass: "text-left",
          formatter: (val, fieldName, item) => {
            return (
              item &&
              item.user &&
              item.user.displayName
            );
          }
        },
        {
          key: "options",
          label: "Options",
          sortable: false,
          class: "text-right"
        }
      ]
    };
  },

  mounted() {
    // When resizing window to a bigger size
    // - call the method to close the modal
    window.addEventListener('resize', this.closeModalOnResize);
    setTimeout(() => {
      this.fetchInitialFilterOptions();
      // Initialize currentSort from store values when component is created
      this.currentSort = {col: this.sortBy, direction: this.sortDirection};
    }, 1000);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.closeModalOnResize);
  },

  computed: {
    ...mapGetters({
      projectId: "project/get/id",
      projectLoading: "project/get/isLoading",
      isPublicOrMember: "project/get/isPublicOrMember",
      isManager: "project/get/isManager",
      isMember: "project/get/isMember",

      observationView: "project/get/observationView",
      observationPage: "project/get/observationPage",
      observationItems: "project/get/observationItems",
      observationTotal: "project/get/observationTotal",
      observationSortBy: "project/get/observationSortBy",
      observationSortDirection: "project/get/observationSortDirection",
      observationSortDesc: "project/get/observationSortDesc",
      observationFilters: "project/get/observationFilters",
    }),
    currentPage: {
      get() {
        return this.observationPage
      },
      set(newValue) {
        this.setObservationTablePage(newValue)
      }
    },
    totalItems: {
      get() {
        return this.observationTotal
      },
      set(newValue) {
        this.setObservationTableTotal(newValue)
      }
    },
    perPage: {
      get() {
        return this.observationItems
      },
      set(newValue) {
        this.setObservationTableItems(newValue)
      }
    },

    isFiltered() {
      return Object.values(this.observationFilters).some(value =>
        Array.isArray(value) ? value.length > 0 :
          typeof value === 'boolean' ? value :
            value !== '' && value !== null
      );
    },

    detailObservationIds() {
      return (this.observationCards || []).map(obs => obs.id);
    },

    sortBy: {
      get() {
        return this.observationSortBy
      },
      set(newValue) {
        this.setObservationSortBy(newValue)
      }
    },
    sortDirection: {
      get() {
        return this.observationSortDirection
      },
      set(newValue) {
        this.setObservationSortDirection(newValue)
      }
    },
    sortDesc: {
      get() {
        return this.observationSortDesc
      },
      set(newValue) {
        this.setObservationSortDesc(newValue)
      }
    },

    selectedSort: {
      get() {
        return this.currentSort
      },
      set(newVal) {
        // Only update if the sort values have actually changed
        if (this.currentSort.col !== newVal.col || this.currentSort.direction !== newVal.direction) {
          this.currentSort = newVal
          this.sortBy = newVal.col
          this.sortDirection = newVal.direction
          this.sortDesc = newVal.direction === 'desc'
        }
      }
    },

    fromItem() {
      return this.totalItems > 0
        ? (this.currentPage - 1) * this.perPage + 1
        : 0;
    },
    toItem() {
      return this.totalItems < this.currentPage * this.perPage
        ? this.totalItems
        : this.currentPage * this.perPage;
    },

    filterVal: {
      get() {
        if (!this.observationFilters) return '';

        let currentFilters = this.observationFilters;
        console.log('current filters: ', currentFilters.hasPhotos)
        let filterUrlParams = [];

        if (currentFilters.locationNames && currentFilters.locationNames.length > 0) {
          currentFilters.locationNames.forEach(loc => {
            filterUrlParams.push('location.name[]=' + encodeURIComponent(loc.name));
          });
        }
        if (currentFilters.datasheetNames && currentFilters.datasheetNames.length > 0) {
          currentFilters.datasheetNames.forEach(ds => {
            filterUrlParams.push('datasheet.name[]=' + encodeURIComponent(ds.name));
          });
        }
        if (currentFilters.year) {
          // Only a date filter is available on the api
          //  - we cheat by setting the date range for the year
          const yr = parseInt(currentFilters.year, 10);
          filterUrlParams.push('observedAt[after]=' + encodeURIComponent(yr + '-01-01'));
          filterUrlParams.push('observedAt[strictly_before]=' + encodeURIComponent((yr + 1) + '-01-01'));
        }
        if (currentFilters.memberNames && currentFilters.memberNames.length > 0) {
          currentFilters.memberNames.forEach(member => {
            filterUrlParams.push('user.displayName[]=' + encodeURIComponent(member.displayName));
          });
        }
        if (currentFilters.hasPhotos) {
          filterUrlParams.push('exists[featuredPhoto]=true');
          if (this.observationView !== 'card' && !this.isPhotoCard) {
            this.setObservationView('card')
            this.isPhotoCard = true
          }
        } else {
          this.isPhotoCard = false
        }
        return filterUrlParams.join('&');
      }
    },
  },
  methods: {
    ...mapActions({
      fetchObservations: "observation/list/getObservationsList",
      deleteObservation: "observation/show/deleteObservation",

      setObservationView: "project/get/setObservationView",
      setObservationTablePage: "project/get/setObservationTablePage",
      setObservationTableItems: "project/get/setObservationTableItems",
      setObservationTableTotal: "project/get/setObservationTableTotal",
      setObservationSortBy: "project/get/setObservationTableSortBy",
      setObservationSortDirection: "project/get/setObservationTableSortDirection",
      setObservationSortDesc: "project/get/setObservationTableSortDesc",
      setObservationTableFilters: "project/get/setObservationTableFilters",
      fetchFilterLocations: "project/get/fetchFilterLocations",
      fetchFilterMembers: "project/get/fetchFilterMembers",
    }),
    observations(ctx) {
      this.isBusy = true;
      let urlParams = `${this.urlBuilder(ctx)}`;
      const data = {id: this.projectId, urlParams};
      return this.fetchObservations(data).then(data => {
        this.totalItems = data["hydra:totalItems"];
        this.currentPage = ctx.currentPage || 1;
        this.sortBy = ctx.sortBy || "";
        this.isBusy = false;
        this.observationCards = data["hydra:member"].map(observation => {
          if (!observation.featuredPhoto) {
            observation.featuredPhoto = {path: "/assets/images/citsci-logo-default.jpg"}
          }
          return {...observation}
        })
        return data["hydra:member"].map(observation => {
          return {
            ...observation
          };
        });
      });
    },

    closeModalOnResize() {
      // 992px is the start of Bootstrap's 'lg' breakpoint.
      // If the screen gets wider than this, forcefully hide the mobile modal.
      if (window.innerWidth >= 992) {
        this.$bvModal.hide('observationFilterModal');
      }
    },

    fetchInitialFilterOptions() {
      this.fetchFilterLocations('')
        .then(locations => {
          this.initialFilterLocations = locations;
        })
        .catch(() => {
        });
      this.fetchFilterMembers('')
        .then(members => {
          this.initialFilterMembers = members;
        })
        .catch(() => {
        });
    },

    handleFilters(val) {
      this.setObservationTableFilters(val.activeState);
      this.$bvModal.hide('observationFilterModal');
    },

    showObservationDetail(id) {
      this.currentDetailObservationId = id;
      this.$bvModal.show('observation-details-modal');
    },
    toObservationPage(observationId) {
      let {href} = this.$router.resolve({name: 'ObservationShow', params: {id: observationId}});
      window.open(href, '_blank', 'noopener,noreferrer');
    },
    closeModal() {
      this.$bvModal.hide("deleteObservationModal");
      this.selectedObservation = null;
    },
    onClickDelete(observation) {
      this.selectedObservation = {...observation};
      this.$bvModal.show("deleteObservationModal");
    },
    onConfirmDelete() {
      this.deleteObservation({
        id: this.selectedObservation.id
      }).then(() => {
        this.$refs.observationsTable.refresh();
        // Call parent to update stats
        this.$emit("update-stats", true);
      });
      this.closeModal();
    }
  }
};
</script>

<style scoped lang="scss">
/* Global style: Prevents table blowout on all screen sizes */
.content-divider {
  min-width: 0;
}

/* Desktop style: Only shows the border when the sidebar is visible */
@media (min-width: 992px) {
  .content-divider {
    border-left: 1px solid #5a5b54;
    padding-left: 1.5rem; /* Adds a nice gap between the line and the table */
  }
}

.filter-container {
  background-color: #f7f6f6;
  border: solid 1px #d6d6d6;
}
</style>
